/**
 * Base Shop Container -  is the main "brain" of shopping pages,
 * here you will find most of the logic that is used on individual shopping pages
 */
import React, { Component, createRef, Fragment } from 'react';
// workaround for IE (https://github.com/abouolia/sticky-sidebar/issues/93_)
import PropTypes from 'prop-types';
import cx from 'classnames';
import queryString from 'query-string';
import { PreauthFooterV3 } from '@zola/zola-ui/src/components/PreAuthV3';

import SectionSeparator from '@zola/component-shopping/src/components/shop/SectionSeparator/SectionSeparator';
import { triggerSrpLinkExperimentViewed } from '@/components/util/triggerSrpLinkExperimentViewed';
import experimentFlags, { CLP_LP_LINKS_TO_SRP } from '@/util/experimentFlags';
import { useLocationForVendorSRPLink } from '@zola/zola-ui/src/hooks/useLocationForVendorSRPLink/useLocationForVendorSRPLink';
import {
  getCategoryEventDetails,
  getFacetsEventDetails,
  trackProductListViewed,
} from '../../util/trackingHelper';
import getProductListViewedEventData from './helpers';
import AlbumOrPaperResults from './components/AlbumOrPaperResults';
import { ScrollToTopButton } from './components/BaseShopContainer/ScrollToTopButton';
import { Header } from './components/BaseShopContainer/Header';
import { Body } from './components/BaseShopContainer/Body';

import { clearBreadcrumbsStorage } from '../ProductDetailPage/helpers';

import {
  FACETS_PAYLOAD_KEY_MAP,
  SORT_OPTIONS,
  TAXONOMY_FACET,
  TAXONOMY_FACET_QUERY_KEY,
  QUERY_MAP_TO_STATE,
  STATE_MAP_TO_QUERY,
} from './constants';

import {
  StickySidebarProvider,
  useStickySidebar,
} from './components/BaseShopContainer/StickySidebarContext';

import './ShopLandingContainer.less';

export class BaseShopContent extends Component {
  constructor(props) {
    super(props);
    const { baseItemsSearchPayload } = props;
    // override default state with the values are coming from query params
    const stateFromUrlParams = this.getStateFromUrlParams();
    this.initialState = {
      selectedProductIndex: 0,
      selectedSortValue: null,
      selectedFilters: {},
      frontTaxId: null,
      initialOffset: 0,
      firstPage: 0,
      lastPage: 0,
      limit: baseItemsSearchPayload.limit,
      openSections: {},
      shopEventData: {},
      // flag for showing bottom items loader
      hasMoreItems: false,
      // flag for showing top items loader
      hasTopProductsLoading: false,
      areMobileFiltersOpen: false,
      products: [],
      productTotal: null,
    };

    this.state = {
      facets: [],
      categoryFilter: props.categoryFilter || null,
      ...this.initialState,
      ...stateFromUrlParams,
    };

    this.handleCategorySelect = this.handleCategorySelect.bind(this);
    this.handleClearAll = this.handleClearAll.bind(this);
    this.toggleMobileFilters = this.toggleMobileFilters.bind(this);
    this.handleFilterSelect = this.handleFilterSelect.bind(this);
    this.handleLoadPreviousProducts = this.handleLoadPreviousProducts.bind(this);
    this.handleMarketingModulesRedirect = this.handleMarketingModulesRedirect.bind(this);
    this.handleProductsScroll = this.handleProductsScroll.bind(this);
    this.handleScrollToTile = this.handleScrollToTile.bind(this);
    this.handleSortValueChange = this.handleSortValueChange.bind(this);
    this.handleTileSelect = this.handleTileSelect.bind(this);
    this.endOfShopTilesSpinner = createRef();
    this.observer = new IntersectionObserver(this.handleProductsScroll);
    this.onInit(stateFromUrlParams);

    this.handleHistoryGoBack.bind(this);
    this.handleStickyBarUpdate = this.handleStickyBarUpdate.bind(this);

    this.popStateEventListener = (event) => this.handleHistoryGoBack(event);
  }

  componentDidMount() {
    if (this.endOfShopTilesSpinner && this.endOfShopTilesSpinner.current) {
      this.observer.observe(this.endOfShopTilesSpinner.current);
    }

    window.addEventListener('popstate', this.popStateEventListener);
    clearBreadcrumbsStorage(true);
  }

  componentWillUnmount() {
    if (this.endOfShopTilesSpinner && this.endOfShopTilesSpinner.current) {
      this.observer.unobserve(this.endOfShopTilesSpinner.current);
    }

    const {
      sticky: { destroy: stickyDestroy },
    } = this.props;
    stickyDestroy();

    window.removeEventListener('popstate', this.popStateEventListener);
  }

  onInit(stateFromUrlParams) {
    this.handleLoadMoreProducts().then(() => {
      const { selectedProductIndex } = this.state;
      if (selectedProductIndex > 0) {
        setTimeout(this.handleScrollToTile, 500);
      }
      if (stateFromUrlParams && Object.keys(stateFromUrlParams)) {
        this.setEventData();
      }
    });
  }

  onCategoryChangeCallback(categoryItem, newState) {
    const {
      isCategoryPage,
      onCategoryRedirect,
      sticky: { destroy: stickyDestroy },
    } = this.props;

    stickyDestroy();

    if (isCategoryPage) {
      const { categoryFilter } = this.state;
      window.history.pushState({
        state: { ...newState, products: [], categoryItem },
        search: window.location.search,
        pathname: `/shop/category/${categoryItem.key}`,
      });
      onCategoryRedirect(categoryItem, categoryFilter);
    } else {
      this.setUrlQueryParams();
      this.handleLoadMoreProducts();
    }
  }

  setEventData() {
    const {
      categoryFilter,
      frontTaxId,
      selectedFilters,
      facets,
      selectedSortValue,
      shopEventData,
    } = this.state;
    let newEventData = {};
    if (frontTaxId) {
      const facetCategoryData = getCategoryEventDetails(frontTaxId, categoryFilter);
      newEventData = { ...newEventData, ...facetCategoryData };
    }
    if (selectedSortValue) {
      const { title } = SORT_OPTIONS.find((n) => n.value === selectedSortValue);
      newEventData = { ...newEventData, sort_by_selection: title };
    }
    if (selectedFilters && Object.keys(selectedFilters)) {
      newEventData = { ...newEventData, ...getFacetsEventDetails(selectedFilters, facets) };
    }
    this.setState({ shopEventData: { ...shopEventData, ...newEventData } });
  }

  /**
   * Builds and returns payload to get product with
   */
  getProductSearchPayload() {
    const { baseItemsSearchPayload } = this.props;
    const { frontTaxId, limit, selectedSortValue } = this.state;
    const sortValue = selectedSortValue ? { sorts: [selectedSortValue] } : {};
    const frontTaxObject = frontTaxId ? { front_tax_ids: [frontTaxId] } : {};
    return {
      ...baseItemsSearchPayload,
      ...frontTaxObject,
      ...sortValue,
      limit,
    };
  }

  /**
   * Resets state properties that affect itmes search payload
   */
  getStateResetProps() {
    return {
      firstPage: 0,
      lastPage: 0,
      products: [],
      initialOffset: 0,
      productTotal: null,
    };
  }

  getStateFromUrlParams() {
    const { baseItemsSearchPayload } = this.props;
    const values = queryString.parse(window.location.search);
    const newState = {};
    const facets = {};

    Object.keys(values).forEach((key) => {
      let stateKey = QUERY_MAP_TO_STATE[key];
      const facetKey = FACETS_PAYLOAD_KEY_MAP[stateKey];
      const isTaxonomyKey = key.indexOf(TAXONOMY_FACET_QUERY_KEY) > -1;
      if (isTaxonomyKey) {
        stateKey = key.replace(
          TAXONOMY_FACET_QUERY_KEY,
          QUERY_MAP_TO_STATE[TAXONOMY_FACET_QUERY_KEY]
        );
      }

      if (facetKey || isTaxonomyKey) {
        facets[stateKey] = values[key]
          .split(',')
          .reduce((a, facetValue) => Object.assign(a, { [facetValue]: true }), {});
        newState.selectedFilters = facets;
      } else if (stateKey) {
        newState[stateKey] = values[key];
      }
    });
    if (newState.selectedProductIndex) {
      newState.firstPage = Math.floor(newState.selectedProductIndex / baseItemsSearchPayload.limit);
      newState.lastPage = newState.firstPage;
      newState.initialOffset = baseItemsSearchPayload.limit * newState.firstPage;
    }
    return newState;
  }

  /**
   * Adds query params to url that feed state on page reload
   */
  setUrlQueryParams() {
    const { selectedFilters } = this.state;
    const { activeCategoryKey, searchQuery } = this.props;
    const queries = [];

    if (searchQuery) {
      const query = `q=${searchQuery}`;
      queries.push(query);
    }

    Object.keys(STATE_MAP_TO_QUERY).forEach((key) => {
      // eslint-disable-next-line react/destructuring-assignment
      const value = this.state[key];
      if (value) {
        const str = `${STATE_MAP_TO_QUERY[key]}=${value}`;
        queries.push(str);
      }
    });

    Object.keys(selectedFilters).forEach((key) => {
      const value = Object.keys(selectedFilters[key]).join(',');
      let queryKey = STATE_MAP_TO_QUERY[key];
      if (key.indexOf(TAXONOMY_FACET) > -1) {
        queryKey = key.replace(TAXONOMY_FACET, TAXONOMY_FACET_QUERY_KEY);
      }
      const str = `${queryKey}=${value}`;
      queries.push(str);
    });

    const resultQueries = queries.length ? `?${queries.join('&')}` : '';
    const path = window.location.pathname;
    const state = { ...this.state, products: [], categoryItem: { key: activeCategoryKey } };
    window.history.pushState(
      { state, pathname: path, search: encodeURI(resultQueries) },
      undefined,
      `${path}${encodeURI(resultQueries)}`
    );
  }

  /**
   * Gets fitler data formatted for products search payload
   * @param {Object} selectedFilters - object with fitler key mapped to object with selected values
   * for this filter (e.g. {BRANDS: {id_1: true, id_2: true}})
   * @returns {Object} - part of payload that corresponds to fitlering by selected parameters
   */
  getFacetsPayload(selectedFilters) {
    const facetsPayload = {};
    const taxonomyFacetGroup = [];
    Object.keys(selectedFilters).forEach((key) => {
      const values = Object.keys(selectedFilters[key]);
      if (values.length > 0 && FACETS_PAYLOAD_KEY_MAP[key]) {
        facetsPayload[FACETS_PAYLOAD_KEY_MAP[key]] = key === 'SALE' ? true : values;
        // taxonomy facets handled differently because its a collection of arrays with ids
      } else if (values.length > 0 && key.indexOf(TAXONOMY_FACET) > -1) {
        taxonomyFacetGroup.push(values);
        facetsPayload[FACETS_PAYLOAD_KEY_MAP[TAXONOMY_FACET]] = taxonomyFacetGroup;
      }
    });
    return facetsPayload;
  }

  /** ****************
   *
   * ACTIONS HANDLERS
   *
   **************** */
  handleMarketingModulesRedirect(index) {
    this.setState(
      {
        selectedProductIndex: index,
      },
      this.setUrlQueryParams
    );
  }

  /**
   * Updates state when going back in browser history
   * @param {Object} state - state saved on url change in location,
   * is used to restore state when going back in browser history
   */
  handleHistoryGoBack(event) {
    const {
      // event.state = from the push state
      // event.state.state = the state we pushed
      state: { state },
    } = event;

    const { isCategoryPage, onCategoryRedirect } = this.props;
    const stateFromUrlParams = this.getStateFromUrlParams();
    const locationState = state || {};
    const newState = { ...this.initialState, ...locationState, ...stateFromUrlParams };
    if (isCategoryPage) {
      if (newState.categoryItem && newState.categoryFilter) {
        onCategoryRedirect(newState.categoryItem, newState.categoryFilter);
      } else {
        window.location.reload();
        return;
      }
    }
    this.setState(newState, () => {
      this.onInit(stateFromUrlParams);
    });
  }

  /**
   * Main handler of Infinite scroll functionality.
   * Is fired when reaching end of loaded tiles.
   * */
  handleProductsScroll(entries) {
    let isLeaving = false;
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        isLeaving = true;
        // flag for tracking of item positions for segment events
        const userScrolled = true;
        this.handleLoadMoreProducts(userScrolled);
        // prevent from firing event more then once, when end of
        // scrollable list is reached and new tiles started loading
      } else if (isLeaving) {
        isLeaving = false;
      }
    });
  }

  /**
   * Scrolls content to target product tile
   */
  handleScrollToTile() {
    const { selectedProductIndex } = this.state;
    if (selectedProductIndex > 0) {
      const productId = `js-shop-product-tile-${selectedProductIndex}`;
      const productTile = document.getElementById(productId);
      productTile.scrollIntoView({ block: 'center' });
      this.setState({ selectedProductIndex: 0 });
    }
  }

  /**
   * Loads one previous "page" for current product selection and remembers current position
   * of the products so it is not scrolled up as more products loaded above.
   * Fires when "Load Previous" button is clicked.
   */
  handleLoadPreviousProducts() {
    this.setState({ hasTopProductsLoading: true });
    const {
      getItemsCallback,
      baseItemsSearchPayload,
      sticky: { update: stickyUpdate },
    } = this.props;
    const { initialOffset, firstPage, products } = this.state;

    const newOffset = Math.max(
      firstPage * baseItemsSearchPayload.limit - baseItemsSearchPayload.limit,
      0
    );
    const updatedLimit = Math.min(baseItemsSearchPayload.limit, initialOffset);
    const basePayload = this.getProductSearchPayload();

    // remember location of the tile below "load previous" button and keep it in view once previous "page" is loaded
    let tileClass = `js-shop-product-tile-${firstPage * 24}`;
    const initialLocation =
      document.getElementById(tileClass).getBoundingClientRect().top +
      document.body.getBoundingClientRect().top;

    getItemsCallback({ ...basePayload, offset: newOffset, limit: updatedLimit }).then((res) => {
      const items = [].concat(...res.items, ...products);
      this.setState(
        {
          selectedProductIndex: 0,
          products: items,
          initialOffset: newOffset,
          firstPage: firstPage - 1,
          hasTopProductsLoading: false,
        },
        () => {
          // come back to the original location so the user is not scrolled to the top of the loaded tiles
          if (firstPage === 1) {
            // long story short: amount of the itmes reterned from api is not reliable,
            // to make sure to keep in view item from the current "page" when loading previous "page"
            // just get the index of the next item after the newly received items
            tileClass = `js-shop-product-tile-${res.items.length}`;
          }

          const currentTileLocation = document
            .getElementById(tileClass)
            .getBoundingClientRect().top;
          window.scrollTo(0, currentTileLocation - initialLocation);
          setTimeout(() => {
            stickyUpdate();
          }, 200);
        }
      );
    });
  }

  handleLoadMoreProducts(userScrolled) {
    const { getItemsCallback, baseItemsSearchPayload } = this.props;
    const { products, lastPage, categoryFilter, selectedFilters, categoryChanged } = this.state;
    const facetsPayload = this.getFacetsPayload(selectedFilters);
    const newOffset = lastPage * baseItemsSearchPayload.limit;
    const basePayload = this.getProductSearchPayload();
    const prevTrackedProductIndex = products.length;
    const prevCategoryChanged = categoryChanged;

    return getItemsCallback({ ...basePayload, offset: newOffset, ...facetsPayload })
      .then((res) => {
        // category filter (aka category tree) doesn't change, so set it once and forget about it!
        if (!categoryFilter && res.category_filter) {
          this.setState({ categoryFilter: res.category_filter.children });
        }
        const hasMoreItems =
          res.total > baseItemsSearchPayload.limit &&
          lastPage < Math.ceil(res.total / baseItemsSearchPayload.limit);
        const items = [].concat(...products, ...res.items);
        // make sidebar sticky if there at least 15 tiles, otherwise it's ok to keep it static
        return this.setState(
          {
            products: items,
            productTotal: res.total,
            facets: res.facets,
            lastPage: lastPage + 1,
            hasMoreItems,
            initialOffset: newOffset,
            categoryChanged: false,
          },
          () => {
            // Update sticky sidebar so it's aware of the parent container height.
            setTimeout(() => this.handleStickyBarUpdate(res.total));
            // the page loading spinner will move to the top of the page and activate the scroll listener whenever a filter or category is changed
            // to track the event only once we need to check that the user has actually scrolled
            if (userScrolled && !prevCategoryChanged && window.scrollY < 1000) return;
            const trackedProductIndex = userScrolled ? prevTrackedProductIndex : 0;
            const productListViewedEventData = getProductListViewedEventData(
              items,
              trackedProductIndex,
              this.props
            );

            if (productListViewedEventData && productListViewedEventData.products.length > 0) {
              trackProductListViewed(productListViewedEventData);
            }
          }
        );
      })
      .catch((err) => {
        if (err.message.match('is not active')) {
          window.location.href = '/shop/brands';
        } else {
          window.location.href = '/not-found';
        }
      });
  }

  handleStickyBarUpdate(productTotal) {
    const {
      sticky: {
        destroy: stickyDestroy,
        update: stickyUpdate,
        initialize: stickyInitialize,
        isInitialized: isStickyInitialized,
      },
    } = this.props;

    const MIN_TILES_AMOUNT = 8;
    const sidebarToMargin = 49;
    const productContainerHeight =
      document.getElementById('js-shop-products-container') &&
      document.getElementById('js-shop-products-container').clientHeight;
    const sidebarContainerHeight =
      document.getElementById('js-shop-sidebar') &&
      document.getElementById('js-shop-sidebar').clientHeight + sidebarToMargin;
    const isSidebarSticky =
      productContainerHeight > sidebarContainerHeight && productTotal > MIN_TILES_AMOUNT;
    if (!isStickyInitialized && isSidebarSticky) {
      stickyInitialize();
    } else if (!isSidebarSticky) {
      stickyDestroy();
    }

    if (stickyInitialize) {
      // update sticky sidebar so it's aware of the parent container height
      stickyUpdate();
    }
  }

  handleCategorySelect(categoryItem) {
    const { isCategoryPage } = this.props;
    const {
      categoryFilter,
      selectedSortValue,
      shopEventData: { sort_by_selection: eventSortByValue },
    } = this.state;
    const id = !isCategoryPage && categoryItem ? categoryItem.id : null;
    const newShopEventData = getCategoryEventDetails(categoryItem.id, categoryFilter);
    let sortByValue = selectedSortValue;
    if (isCategoryPage) {
      // reset to default for category pages only
      sortByValue = null;
    } else if (selectedSortValue) {
      newShopEventData.sort_by_selection = eventSortByValue;
    }
    const newState = {
      ...this.getStateResetProps(),
      selectedFilters: {},
      frontTaxId: id,
      selectedSortValue: sortByValue,
      shopEventData: newShopEventData,
      hasMoreItems: true,
      categoryChanged: true,
    };
    this.setState(newState, () => {
      this.onCategoryChangeCallback(categoryItem, newState);
    });
  }

  handleFilterSelect() {
    const {
      sticky: { destroy: stickyDestroy },
    } = this.props;
    this.setState(
      {
        ...this.getStateResetProps(),
      },
      () => {
        stickyDestroy();
        this.updateFiltersEventData();
        this.setUrlQueryParams();
        this.handleLoadMoreProducts();
      }
    );
  }

  handleTileSelect(index) {
    const { firstPage } = this.state;
    const selectedProductIndex = index + firstPage * 24;
    window.history.scrollRestoration = 'manual';
    this.setState(
      {
        selectedProductIndex,
      },
      this.setUrlQueryParams
    );
  }

  toggleMobileFilters() {
    const { areMobileFiltersOpen } = this.state;
    this.setState({
      areMobileFiltersOpen: !areMobileFiltersOpen,
    });
  }

  /**
   * Clears selected categories and filters
   */
  handleClearAll() {
    const {
      isCategoryPage,
      sticky: { destroy: stickyDestroy },
    } = this.props;
    const { openSections } = this.state;
    const newOpenSections = isCategoryPage ? openSections : {};
    stickyDestroy();
    this.setState(
      {
        ...this.getStateResetProps(),
        frontTaxId: null,
        selectedFilters: {},
        openSections: newOpenSections,
      },
      () => {
        this.setUrlQueryParams();
        this.handleLoadMoreProducts();
      }
    );
  }

  handleSortValueChange(selectedSortValue) {
    const { shopEventData } = this.state;
    const { title } = SORT_OPTIONS.find((n) => n.value === selectedSortValue);
    const data = { ...shopEventData, sort_by_selection: title };
    this.setState(
      {
        ...this.getStateResetProps(),
        selectedSortValue,
        shopEventData: data,
      },
      () => {
        this.setUrlQueryParams();
        this.handleLoadMoreProducts();
      }
    );
  }

  updateFiltersEventData() {
    const { selectedFilters, facets, shopEventData } = this.state;
    const data = { ...shopEventData, ...getFacetsEventDetails(selectedFilters, facets) };
    this.setState({ shopEventData: data });
  }

  render() {
    const {
      metaData,
      hasNoSearchResults,
      useGridTiles = false,
      activeCategoryKey,
      userContext,
      customTitle,
      newFilteringTitle,
      isCategoryPage,
      isRectangularImage,
      hasCategoryLink,
      transactionIdsMap,
      marketingModules,
      eventData,
      showNoResultsContent = true,
    } = this.props;

    const {
      products,
      facets,
      selectedFilters,
      productTotal,
      areMobileFiltersOpen,
      selectedSortValue,
      openSections,
      frontTaxId,
      firstPage,
      hasTopProductsLoading,
      shopEventData,
      hasMoreItems,
    } = this.state;
    return (
      <Fragment>
        {useGridTiles && <SectionSeparator />}
        <div
          className={cx('shop-body-container', {
            row: !useGridTiles,
            'has-sidebar': !hasNoSearchResults,
          })}
        >
          <Body
            activeCategoryKey={activeCategoryKey}
            areMobileFiltersOpen={areMobileFiltersOpen}
            // This uses state.categoryFilter, other places use props.  We probably
            // want state, but the choice was made during refactoring to make no changes
            // eslint-disable-next-line react/destructuring-assignment
            categoryFilter={this.state.categoryFilter}
            customTitle={customTitle}
            endOfShopTilesSpinner={this.endOfShopTilesSpinner}
            eventData={eventData}
            facets={facets}
            firstPage={firstPage}
            frontTaxId={frontTaxId}
            handleCategorySelect={this.handleCategorySelect}
            handleClearAll={this.handleClearAll}
            handleFilterSelect={this.handleFilterSelect}
            handleLoadPreviousProducts={this.handleLoadPreviousProducts}
            handleMarketingModulesRedirect={this.handleMarketingModulesRedirect}
            handleSortValueChange={this.handleSortValueChange}
            handleTileSelect={this.handleTileSelect}
            hasCategoryLink={hasCategoryLink}
            hasMoreItems={hasMoreItems}
            showNoResultsContent={showNoResultsContent}
            hasNoSearchResults={hasNoSearchResults}
            hasTopProductsLoading={hasTopProductsLoading}
            isCategoryPage={isCategoryPage}
            isRectangularImage={isRectangularImage}
            marketingModules={marketingModules}
            metaData={metaData}
            newFilteringTitle={newFilteringTitle}
            openSections={openSections}
            products={products}
            productTotal={productTotal}
            selectedFilters={selectedFilters}
            selectedSortValue={selectedSortValue}
            setFilters={(value) => this.setState({ selectedFilters: value })}
            setOpenSections={(value) => this.setState({ openSections: value })}
            shopEventData={shopEventData}
            toggleMobileFilters={this.toggleMobileFilters}
            transactionIdsMap={transactionIdsMap}
            useGridTiles={useGridTiles}
            userContext={userContext}
          />
        </div>
      </Fragment>
    );
  }
}

export const BaseShopContainerWithSticky = (props) => {
  const { destroy, update, initialize } = useStickySidebar();
  const { searchQuery } = props;

  return (
    <Fragment>
      <AlbumOrPaperResults searchQuery={searchQuery} updateStickySidebar={update} />
      <BaseShopContent {...props} sticky={{ destroy, update, initialize }} />
    </Fragment>
  );
};

export const BaseShopContainer = (props) => {
  const {
    userContext,
    aboveBannerContent,
    metaData,
    categoryFilter,
    weddingNavKey,
    showBanner,
    useGridTiles,
    heroContent,
    collectionKey,
    brandKey,
  } = props;

  const isGuest = userContext ? Boolean(userContext.is_guest) : true;
  const isInSRPExperiment =
    typeof window !== 'undefined' &&
    userContext &&
    !userContext.is_guest &&
    experimentFlags.isUnderTest(CLP_LP_LINKS_TO_SRP, userContext, false);
  const weddingLocation = useLocationForVendorSRPLink({
    isInSRPExperiment: isInSRPExperiment || false,
  });

  return (
    <div
      id="zo-shop"
      className={cx({
        postAuthNav3PageBackground: !isGuest,
      })}
    >
      <StickySidebarProvider
        className={cx({
          postAuthNav3PageWrapper: !isGuest,
        })}
      >
        <Header
          aboveBannerContent={aboveBannerContent}
          metaData={metaData}
          categoryFilter={categoryFilter}
          weddingNavKey={weddingNavKey}
          showBanner={showBanner}
          collectionKey={collectionKey}
          brandKey={brandKey}
        />

        {/* Target for a side drawer in the nav */}
        <div id="add-item-drawer" />
        <ScrollToTopButton>
          <div
            id="zola-shop-gift-collection"
            className={cx(useGridTiles ? 'gridVersion' : 'row', {
              isPostAuthNav3Shown: !isGuest,
            })}
          >
            {heroContent}
            <BaseShopContainerWithSticky {...props} />
          </div>
        </ScrollToTopButton>
      </StickySidebarProvider>
      <div className={cx({ postAuthNav3Padding: !isGuest })}>
        <PreauthFooterV3
          weddingLocation={weddingLocation}
          isGuest={isGuest}
          marketplacePostAuthLinkOnClick={() => {
            triggerSrpLinkExperimentViewed(userContext);
          }}
        />
      </div>
    </div>
  );
};

BaseShopContainer.propTypes = {
  baseItemsSearchPayload: PropTypes.shape({}),
  userContext: PropTypes.shape({}),
  categoryFilter: PropTypes.arrayOf(PropTypes.shape({})),
  collectionKey: PropTypes.string,
  brandKey: PropTypes.string,
  eventData: PropTypes.shape({}),
  metaData: PropTypes.shape({}),
  marketingModules: PropTypes.shape({}),
  // callbacks
  getItemsCallback: PropTypes.func,
  onCategoryRedirect: PropTypes.func,
  // html content
  aboveBannerContent: PropTypes.node,
  heroContent: PropTypes.node,
  // primitives
  isCategoryPage: PropTypes.bool,
  hasCategoryLink: PropTypes.bool,
  isRectangularImage: PropTypes.bool,
  hasNoSearchResults: PropTypes.bool,
  transactionIdsMap: PropTypes.shape({}),
  activeCategoryKey: PropTypes.string,
  customTitle: PropTypes.string,
  weddingNavKey: PropTypes.string,
  searchQuery: PropTypes.string,
  useGridTiles: PropTypes.bool,
  showNoResultsContent: PropTypes.bool,
  newFilteringTitle: PropTypes.string,
  showBanner: PropTypes.bool,
};

export default BaseShopContainer;
